'use client';

import { useIsHydrated } from '@7f-ui/hooks/use-is-hydrated';
import { parseLocalDate, parseLocalDay } from '@utils/helpers';
import { NON_CANCELABLE } from '@utils/utils-config';
import { formatDate, formatTime } from '@utils/utils-format';
import { isBefore, subHours } from 'date-fns';
import { useState } from 'react';
import { DEFAULT_TIME_ZONE } from '@/lib/time-zone';
type CancelationDeadline = {
  local?: string | null;
  timezone?: string | null;
} | null | undefined;
export function getCancelationState(deadline: CancelationDeadline, now: Date | null = null): {
  date: Date | null;
  zone: string;
  isCancelable: boolean | null;
} {
  const zone = deadline?.timezone ?? DEFAULT_TIME_ZONE;
  if (!deadline?.local) return {
    date: null,
    zone,
    isCancelable: false
  };
  const date = parseLocalDate({
    local: deadline.local,
    timezone: deadline.timezone
  });
  return {
    date,
    zone,
    isCancelable: now ? isBefore(now, date) : null
  };
}
type ExperienceCancelationItem = {
  timeslot?: {
    startDate: {
      local: string;
      timezone?: string | null;
    };
  } | null;
  visitDate?: string | null;
  experience: {
    cancelationDeadline: number;
  };
};

/**
 * Cancelation state of an experience item: the deadline counts back from the timeslot start, or
 * from 00:00 Berlin time of the visit day for visit-day tickets.
 * Returns `null` for undated tickets with a deadline: the backend counts those from the booking
 * time, so there is no fixed deadline to show.
 */
export function getExperienceCancelationState(item: ExperienceCancelationItem, now: Date | null = null): ReturnType<typeof getCancelationState> | null {
  const zone = item.timeslot?.startDate.timezone ?? DEFAULT_TIME_ZONE;
  const hours = item.experience.cancelationDeadline;
  if (hours >= NON_CANCELABLE) return {
    date: null,
    zone,
    isCancelable: false
  };
  let reference: Date | null = null;
  if (item.timeslot) {
    reference = parseLocalDate(item.timeslot.startDate);
  } else if (item.visitDate) {
    // The backend currently counts visit days from 00:00 UTC (`new Date(visitDate)`,
    // booking-item.service.ts:890). The shop deliberately states the earlier Berlin-midnight
    // deadline so it never promises more than the backend honours.
    reference = parseLocalDay(item.visitDate);
  }
  if (!reference) return null;
  const date = subHours(reference, hours);
  return {
    date,
    zone,
    isCancelable: now ? isBefore(now, date) : null
  };
}
export const useHydratedNow = () => {
  const isHydrated = useIsHydrated();
  const [now] = useState(_temp);
  return isHydrated ? now : null;
};
export const useCancelationDateFormat = (date?: Date | null, timezone?: string | null): {
  time: string;
  date: string;
} | null => {
  const zone = timezone ?? DEFAULT_TIME_ZONE;
  if (!date) return null;
  return {
    time: formatTime(date, zone),
    date: formatDate(date, zone)
  };
};
function _temp() {
  return new Date();
}